import { default as React } from 'react';
import { useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
  useSkyStageAdminEmails,
  useMicroappEnvironmentStatus,
} from './hooks';
import { PodLogsModal } from './PodLogsModal';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';
const MICROAPP_ANNOTATION_VALUE = 'microapp';

export const ViewPodLogsButton = () => {
  const { entity } = useEntity();
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();
  const [prodModalOpen, setProdModalOpen] = useState(false);
  const [stagingModalOpen, setStagingModalOpen] = useState(false);

  const appName = entity.metadata.name;

  // Check if this is a microapp
  const isMicroapp =
    entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
    MICROAPP_ANNOTATION_VALUE;

  // Check if this is a Hosted app (only Hosted apps have ArgoCD deployments)
  const isHostedApp =
    entity.metadata.annotations?.['microapp/appType'] === 'Hosted';

  // Authorized if the user owns/created the app or is a SkyStage admin.
  const isAuthorized = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  // IDP-983: Check if staging is enabled
  const stagingEnabled =
    entity.metadata.annotations?.['microapp/stagingEnabled'] === 'true';

  // Get environment status to know which envs are deployed
  const { status } = useMicroappEnvironmentStatus(appName);
  const stagingDeployed = status?.staging?.deployed ?? false;
  const productionDeployed = status?.production?.deployed ?? false;
  // IDP-1073: Check if environments are scaled to zero (no running pods)
  const stagingScaledToZero = status?.staging?.scaledToZero ?? false;
  const productionScaledToZero = status?.production?.scaledToZero ?? false;

  // Get ArgoCD app name from annotation (production)
  // The annotation contains the full cluster-prefixed name (e.g., plateng-prod-qp-gke-myapp)
  // IDP-1073: Defensive fix - strip "-staging" suffix if present, since the annotation
  // should always be the production app name. This handles cases where promotion
  // didn't correctly update the annotation or catalog refresh issues.
  const rawArgoCdAppName = entity.metadata.annotations?.['argocd/app-name'];
  const prodArgoCdAppName = rawArgoCdAppName?.endsWith('-staging')
    ? rawArgoCdAppName.slice(0, -8) // Remove "-staging" suffix
    : rawArgoCdAppName;
  // Staging ArgoCD app name = production name + "-staging" suffix (same pattern as MicroappArgoCDCard)
  const stagingArgoCdAppName = prodArgoCdAppName
    ? `${prodArgoCdAppName}-staging`
    : undefined;

  // Namespaces
  const productionNamespace = `qp-${appName}-ns`;
  const stagingNamespace = `qp-${appName}-staging-ns`;

  // Only render for authorized users on Hosted microapps with required data
  if (!isMicroapp || !isHostedApp || !isAuthorized || !prodArgoCdAppName) {
    return null;
  }

  // IDP-1073: Button visibility and disabled states:
  // 1. No staging → no "View Staging Logs" button
  // 2. Staging with zero replicas → button visible but disabled
  // 3. View prod log only when prod is deployed
  // 4. Both buttons when both environments exist
  return (
    <Box display="flex" style={{ gap: 8 }}>
      {/* Production Logs button - shown when production is deployed */}
      {productionDeployed && (
        <>
          <Tooltip
            title={
              productionScaledToZero
                ? 'Pod is scaled to zero — no logs available'
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<DescriptionIcon />}
                onClick={() => setProdModalOpen(true)}
                disabled={productionScaledToZero}
                sx={{ textTransform: 'none' }}
              >
                View Pod Logs
              </Button>
            </span>
          </Tooltip>

          <PodLogsModal
            open={prodModalOpen}
            onClose={() => setProdModalOpen(false)}
            appName={appName}
            argoCdAppName={prodArgoCdAppName}
            namespace={productionNamespace}
          />
        </>
      )}

      {/* Staging Logs button - shown when staging is enabled AND deployed */}
      {stagingEnabled && stagingDeployed && stagingArgoCdAppName && (
        <>
          <Tooltip
            title={
              stagingScaledToZero
                ? 'Staging is scaled to zero — no logs available'
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DescriptionIcon />}
                onClick={() => setStagingModalOpen(true)}
                disabled={stagingScaledToZero}
                sx={{
                  textTransform: 'none',
                  borderColor: stagingScaledToZero ? undefined : '#e65100',
                  color: stagingScaledToZero ? undefined : '#e65100',
                  '&:hover': {
                    borderColor: stagingScaledToZero ? undefined : '#bf360c',
                    backgroundColor: stagingScaledToZero
                      ? undefined
                      : 'rgba(230, 81, 0, 0.04)',
                  },
                }}
              >
                View Staging Logs
              </Button>
            </span>
          </Tooltip>

          <PodLogsModal
            open={stagingModalOpen}
            onClose={() => setStagingModalOpen(false)}
            appName={appName}
            argoCdAppName={stagingArgoCdAppName}
            namespace={stagingNamespace}
          />
        </>
      )}
    </Box>
  );
};
